import { useRef, useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { Menu } from '@highlight/ui'
import { FeedbackPopover, type FeedbackSource } from '@renderer/components/feedback'
import { useMessageFeedback } from '@renderer/components/feedback/use-message-feedback'
import { feedbackSubjectKey, type FeedbackSubject } from '@renderer/chat/api/feedback'
import { CopyBraintrustLinkMenuItem } from './copy-braintrust-link-menu-item'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'

/** Combined up/down thumbs — the one glyph standing in for the trigger pair. */
function FeedbackThumbsIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="size-3.5">
      <path
        d="M3.11089 4.44423V9.77756M6.66645 2.61312L6.22201 4.44423H8.81312C8.95111 4.44423 9.08721 4.47636 9.21064 4.53807C9.33407 4.59978 9.44143 4.68939 9.52423 4.79978C9.60703 4.91018 9.66298 5.03834 9.68767 5.17411C9.71235 5.30988 9.70509 5.44953 9.66645 5.58201L8.63089 9.13756C8.57704 9.3222 8.46476 9.48439 8.31089 9.59978C8.15703 9.71518 7.96989 9.77756 7.77756 9.77756H1.77756C1.54181 9.77756 1.31572 9.68391 1.14902 9.51721C0.982322 9.35051 0.888672 9.12442 0.888672 8.88867V5.33312C0.888672 5.09737 0.982322 4.87128 1.14902 4.70458C1.31572 4.53788 1.54181 4.44423 1.77756 4.44423H3.00423C3.1696 4.44414 3.33167 4.39792 3.47221 4.31077C3.61275 4.22362 3.72619 4.09899 3.79978 3.95089L5.33312 0.888672C5.54271 0.891267 5.749 0.941191 5.93659 1.03471C6.12417 1.12824 6.2882 1.26294 6.41641 1.42876C6.54462 1.59458 6.6337 1.78723 6.677 1.99232C6.7203 2.1974 6.71669 2.40962 6.66645 2.61312Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.2227 5.59766C14.624 5.59775 15.0091 5.75719 15.293 6.04102C15.5768 6.32486 15.7363 6.70994 15.7363 7.11133V10.667C15.7362 11.0683 15.5766 11.4535 15.293 11.7373C15.0092 12.0209 14.6239 12.1806 14.2227 12.1807H12.9961C12.9472 12.1808 12.899 12.195 12.8574 12.2207C12.816 12.2465 12.7815 12.2834 12.7598 12.3271L12.7588 12.3291L11.2256 15.3916C11.1183 15.6053 10.8983 15.7392 10.6592 15.7363C10.3555 15.7325 10.0559 15.6599 9.78418 15.5244C9.51259 15.3889 9.27455 15.1942 9.08887 14.9541C8.90311 14.7139 8.77371 14.4338 8.71094 14.1367C8.64844 13.8404 8.65339 13.5333 8.72559 13.2393L8.9834 12.1807H7.18652C6.95175 12.1806 6.71977 12.1265 6.50977 12.0215C6.29963 11.9164 6.11657 11.7631 5.97559 11.5752C5.94606 11.5358 5.91998 11.494 5.89453 11.4521H7.77832C8.33288 11.4521 8.87272 11.2722 9.31641 10.9395C9.32011 10.9367 9.32347 10.9335 9.32715 10.9307H9.77832C9.96985 10.9308 10.1511 11.0193 10.2695 11.1699C10.3878 11.3205 10.4299 11.517 10.3848 11.7031L9.94043 13.5342V13.5371C9.91289 13.649 9.91076 13.7661 9.93457 13.8789C9.95845 13.9917 10.0076 14.0982 10.0781 14.1895C10.1447 14.2754 10.2293 14.3456 10.3252 14.3965L11.6416 11.7695C11.7669 11.5183 11.9596 11.3063 12.1982 11.1582C12.2192 11.1452 12.2422 11.1349 12.2637 11.123V6.84766H11.043L11.2754 6.05078L11.3125 5.90723C11.336 5.8051 11.3514 5.70158 11.3623 5.59766H14.2227ZM13.5137 10.9307H14.2227C14.2923 10.9306 14.3598 10.9027 14.4092 10.8535C14.4584 10.8041 14.4862 10.7367 14.4863 10.667V7.11133C14.4863 7.04146 14.4586 6.97423 14.4092 6.9248C14.3598 6.8754 14.2925 6.84775 14.2227 6.84766H13.5137V10.9307Z"
        fill="currentColor"
      />
    </svg>
  )
}

interface FeedbackMenuButtonProps {
  subject: FeedbackSubject
  source: FeedbackSource
  currentRating?: 'positive' | 'negative' | null
  /** Dev-only affordance — adds a copy item for the authoring turn's trace. */
  braintrustLink?: string
  /** Archives the content after negative feedback succeeds when the user opts in. */
  onArchive?: () => void | Promise<void>
}

/**
 * One feedback circle in place of the thumbs pair: a popover menu picks the
 * rating, and the shared comment popover takes it from there — the same
 * submission, confirmation and close handling the thumb triggers get.
 */
export function FeedbackMenuButton(props: FeedbackMenuButtonProps) {
  return <FeedbackMenuButtonSession key={feedbackSubjectKey(props.subject)} {...props} />
}

function FeedbackMenuButtonSession({
  subject,
  source,
  currentRating,
  braintrustLink,
  onArchive,
}: FeedbackMenuButtonProps) {
  // Anchors the rating popover to the circle the menu opened from. A wrapper
  // rather than a ref on the trigger: `Menu.Trigger` is a plain function
  // component, so a ref handed to it would go nowhere on React 18.
  const anchorRef = useRef<HTMLDivElement>(null)
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null)
  const feedback = useMessageFeedback(subject, currentRating ?? null)

  return (
    <div ref={anchorRef} className="flex shrink-0 items-center">
      <Menu>
        <Menu.Trigger variant="outline" shape="square" aria-label="Give feedback" className={FLOATING_FILL_CLASS}>
          {feedback.rating === 'positive' ? (
            <ThumbsUp />
          ) : feedback.rating === 'negative' ? (
            <ThumbsDown />
          ) : (
            <FeedbackThumbsIcon />
          )}
        </Menu.Trigger>
        <Menu.Content side="top" align="end" className="w-48">
          <Menu.Item onClick={() => setFeedbackType('positive')}>
            <ThumbsUp />
            <span>Good response</span>
          </Menu.Item>
          <Menu.Item onClick={() => setFeedbackType('negative')}>
            <ThumbsDown />
            <span>Bad response</span>
          </Menu.Item>
          {/* Staff-only affordance, last so it never sits between the two ratings. */}
          <CopyBraintrustLinkMenuItem link={braintrustLink} />
        </Menu.Content>
      </Menu>
      <FeedbackPopover
        feedback={feedback}
        source={source}
        type={feedbackType}
        onClose={() => setFeedbackType(null)}
        anchor={anchorRef}
        onArchive={onArchive}
      />
    </div>
  )
}
